import { useMemo, type ReactNode } from 'react';
import { Logo, cn } from '@/components/ui';
import { getUnitsLabel, isUnitsSpecified } from '@/components/common/units';
import {
  PLACEHOLDER,
  formatAzimuth,
  formatConfidence,
  formatDMS,
  formatFixed,
  formatResidual,
  formatScale,
} from '@/logic/statistics';
import {
  deriveBlunderVerdict,
  type FlaggedPoint,
} from '@/logic/adjustments/blunderVerdict';
import { useReportInput } from '@/store/selectors';
import { prepareReportInput } from '@/utils/reportInput';
import { LIMITATION_TEXT, MODE_LABELS } from '@/utils/reportLog';
import type {
  BlunderDetectionResults,
  DataSnoopingResults,
  PointPair,
} from '@/types';

/* Print-only building blocks: hairline rules, small mono figures. */

const Section = ({
  number,
  title,
  children,
}: {
  number: number;
  title: string;
  children: ReactNode;
}) => (
  <section className="mt-7">
    <h2 className="mb-2 flex items-baseline gap-2 border-b border-fg pb-1 text-base font-semibold break-after-avoid">
      <span className="font-mono text-sm text-fg-muted">{number}</span>
      {title}
    </h2>
    {children}
  </section>
);

const Th = ({
  children,
  numeric = false,
}: {
  children?: ReactNode;
  numeric?: boolean;
}) => (
  <th
    scope="col"
    className={cn(
      'border-b border-fg px-2 pb-1 pt-1.5 text-[11px] font-medium uppercase tracking-wide text-fg-muted first:pl-0 last:pr-0',
      numeric ? 'text-right' : 'text-left'
    )}
  >
    {children}
  </th>
);

const Td = ({
  children,
  numeric = false,
  mono = numeric,
  className,
}: {
  children: ReactNode;
  numeric?: boolean;
  mono?: boolean;
  className?: string;
}) => (
  <td
    className={cn(
      'border-b border-border-strong px-2 py-1 align-top text-xs first:pl-0 last:pr-0',
      mono && 'font-mono tabular-nums',
      numeric ? 'text-right' : 'text-left',
      className
    )}
  >
    {children}
  </td>
);

const DataTable = ({ children }: { children: ReactNode }) => (
  <table className="w-full border-collapse">{children}</table>
);

/** Two-column label/value rows for parameters. */
const Row = ({
  label,
  value,
  note,
}: {
  label: ReactNode;
  value: ReactNode;
  note?: ReactNode;
}) => (
  <tr>
    <Td className="w-60 text-fg-muted">{label}</Td>
    <Td mono className="w-48">
      {value}
    </Td>
    <Td className="text-fg-muted">{note}</Td>
  </tr>
);

/** A standard deviation note, e.g. "σ 0.00038346". */
const sigma = (value: string) => (
  <>
    σ <span className="font-mono tabular-nums">{value}</span>
  </>
);

const names = (points: FlaggedPoint[]) =>
  points.map((point) => point.label).join(', ');

const VerdictText = ({
  blunderResults,
  dataSnoopingResults,
  pointPairs,
}: {
  blunderResults: BlunderDetectionResults | null;
  dataSnoopingResults: DataSnoopingResults | null;
  pointPairs: PointPair[];
}) => {
  const verdict = deriveBlunderVerdict(
    blunderResults,
    dataSnoopingResults,
    pointPairs
  );
  let summary: ReactNode;
  switch (verdict.state) {
    case 'clean':
      summary =
        'No blunders detected — both methods agree the data is consistent.';
      break;
    case 'agree':
      summary = `High confidence — both methods flag ${names(verdict.agreed)}.`;
      break;
    case 'inconclusive':
      summary = (
        <>
          Inconclusive — the matching results are not evidence of clean data.
          {verdict.agreed.length > 0 &&
            ` Flagged by both: ${names(verdict.agreed)}.`}
        </>
      );
      break;
    case 'differ':
      summary = (
        <>
          Methods disagree.
          {verdict.agreed.length > 0 &&
            ` Flagged by both (high confidence): ${names(verdict.agreed)}.`}
          {verdict.combinatorialOnly.length > 0 &&
            ` Combinatorial search only: ${names(verdict.combinatorialOnly)}.`}
          {verdict.dataSnoopingOnly.length > 0 &&
            ` Data snooping only: ${names(verdict.dataSnoopingOnly)}.`}
        </>
      );
      break;
    case 'single':
      summary = 'Only one method was run — no cross-check available.';
      break;
    default:
      summary = 'Not run.';
  }
  return (
    <div className="mb-3 text-sm">
      <p>
        <span className="font-semibold">Cross-method verdict: </span>
        {summary}
      </p>
      {verdict.limitations.length > 0 && (
        <ul className="mt-1 space-y-0.5 text-xs text-fg-muted">
          {verdict.limitations.map((limitation) => (
            <li key={limitation} className="first-letter:uppercase">
              Limitation: {LIMITATION_TEXT[limitation]}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const CombinatorialTable = ({
  results,
}: {
  results: BlunderDetectionResults;
}) => {
  if (results.isAllLevels && results.summary) {
    return (
      <>
        <h3 className="mb-1 text-sm font-semibold">
          Combinatorial search — best rejection set per level
        </h3>
        <DataTable>
          <thead>
            <tr>
              <Th numeric>Level</Th>
              <Th>Rejected</Th>
              <Th numeric>Max residual</Th>
              <Th numeric>Azimuth</Th>
              <Th numeric>Rotation</Th>
              <Th numeric>Scale</Th>
            </tr>
          </thead>
          <tbody>
            {results.summary.map((row) => (
              <tr key={row.level}>
                <Td numeric>{row.level}</Td>
                <Td mono>{row.rejectedLabels?.join(', ') || PLACEHOLDER}</Td>
                <Td numeric>{formatFixed(row.maxResidual, 3)}</Td>
                <Td numeric>{formatAzimuth(row.maxResidualAzimuth)}</Td>
                <Td numeric>{formatDMS(row.rotation)}</Td>
                <Td numeric>{formatScale(row.scale, 6)}</Td>
              </tr>
            ))}
          </tbody>
        </DataTable>
      </>
    );
  }
  if (!results.results?.length) return null;
  return (
    <>
      <h3 className="mb-1 text-sm font-semibold">
        Combinatorial search — level {results.rejectionLevel}, best first
      </h3>
      <DataTable>
        <thead>
          <tr>
            <Th>Rejected</Th>
            <Th numeric>Max residual</Th>
            <Th numeric>RMS</Th>
          </tr>
        </thead>
        <tbody>
          {results.results.slice(0, 5).map((row, index) => (
            <tr key={index}>
              <Td mono>
                {row.isInitial ? 'No rejection' : row.rejectedLabels.join(', ')}
              </Td>
              <Td numeric>{formatFixed(row.maxResidual, 3)}</Td>
              <Td numeric>{formatFixed(row.rmsTotal, 3)}</Td>
            </tr>
          ))}
        </tbody>
      </DataTable>
    </>
  );
};

const DataSnoopingSummary = ({ results }: { results: DataSnoopingResults }) => {
  const { alpha, criticalValue, redundancy, testStatistic } = results;
  const tau = testStatistic === 'tau';
  const symbol = tau ? 'τ' : 'w';
  const rejected = results.iterations.filter(
    (iteration) => iteration.rejectedLabel
  );
  return (
    <>
      <h3 className="mb-1 mt-4 text-sm font-semibold">
        Iterative data snooping
      </h3>
      <p className="mb-1.5 text-xs text-fg-muted">
        {tau ? "Pope's τ test" : 'w-test'}, {formatConfidence(alpha)} confidence
        per component
        {criticalValue > 0 &&
          `, first critical value ${formatFixed(criticalValue, 2)}`}
        {redundancy !== undefined && ` at r = ${redundancy}`}.
      </p>
      {rejected.length > 0 && (
        <DataTable>
          <thead>
            <tr>
              <Th numeric>Iteration</Th>
              <Th>Rejected</Th>
              <Th numeric>
                <span className="normal-case">{symbol}</span>
              </Th>
              <Th numeric>Critical</Th>
              <Th numeric>MDB</Th>
            </tr>
          </thead>
          <tbody>
            {rejected.map((iteration) => (
              <tr key={iteration.iteration}>
                <Td numeric>{iteration.iteration}</Td>
                <Td mono>{iteration.rejectedLabel}</Td>
                <Td numeric>
                  {formatFixed(iteration.standardizedResidual, 2)}
                </Td>
                <Td numeric>{formatFixed(iteration.criticalValue, 2)}</Td>
                <Td numeric>
                  {formatFixed(iteration.minimumDetectableBias, 3)}
                </Td>
              </tr>
            ))}
          </tbody>
        </DataTable>
      )}
      <ul className="mt-1.5 space-y-0.5 text-xs">
        {results.stopReason === 'insufficientRedundancy' ? (
          <li>Testing stopped: too little redundancy for the τ test.</li>
        ) : results.stopReason === 'noTestableObservations' ? (
          <li>Testing stopped: no remaining residual could be tested.</li>
        ) : (
          results.finalRejectedIds.length === 0 && (
            <li>All points passed the statistical test.</li>
          )
        )}
        {results.lowPower && (
          <li className="text-fg-muted">
            Low power: a blunder near the MDB could pass undetected.
          </li>
        )}
      </ul>
    </>
  );
};

/**
 * Print-only analysis report. Hidden on screen; `window.print()` renders it
 * as a filed-quality document (the interactive app is print-hidden). It
 * mirrors the analysis log: a stale solution makes the report unavailable,
 * and outdated detection or output sections are omitted with a notice.
 * Print output is always light (dark tokens never apply to print).
 */
export const PrintReport = () => {
  const report = useReportInput();
  const prepared = useMemo(() => prepareReportInput(report), [report]);

  if (!report.solution || report.solution.error) return null;
  if (prepared.status === 'unavailable')
    return (
      <div className="hidden text-sm text-fg print:block">
        Report unavailable: point data changed. Re-solve before printing.
      </div>
    );

  const {
    inputRevision,
    solutionRevision,
    solutionInputRevision,
    appliedScale,
    units = 'unspecified',
    transformedFromRevision,
    solution,
    solutionTimestamp,
    pointPairs,
    blunderResults,
    dataSnoopingResults,
    transformedPoints,
  } = prepared.input;
  if (!solution) return null;
  const omitted = prepared.omittedSections;
  const pairById = new Map(pointPairs.map((pair) => [pair.id, pair]));
  const hasDetection = !!blunderResults || !!dataSnoopingResults;
  let section = 0;

  const meta: Array<[string, ReactNode]> = [
    ['Units', isUnitsSpecified(units) ? getUnitsLabel(units) : 'Not specified'],
    ['Scale setting', appliedScale == null ? 'Estimated' : appliedScale],
    ['Solution mode', MODE_LABELS[solution.mode ?? 'standard']],
    [
      'Revisions',
      `Input ${inputRevision ?? '?'} · solution ${solutionRevision ?? '?'} (input ${solutionInputRevision ?? '?'})${
        transformedPoints.length
          ? ` · output from solution ${transformedFromRevision ?? '?'}`
          : ''
      }`,
    ],
    [
      'Solution computed',
      solutionTimestamp
        ? new Date(solutionTimestamp).toLocaleString()
        : PLACEHOLDER,
    ],
    ['Printed', new Date().toLocaleString()],
  ];

  return (
    <article className="hidden bg-surface text-fg print:block">
      <header className="border-b-2 border-fg pb-3">
        <div className="flex items-center justify-between text-xs text-fg-muted">
          <span className="inline-flex items-center gap-1.5 font-medium text-fg">
            <Logo size={16} className="text-accent" />
            Resolve · Cadastre
          </span>
          <span>Analysis report</span>
        </div>
        <h1 className="mt-3 text-2xl font-semibold tracking-tight">
          Coordinate Transformation &amp; Blunder Analysis
        </h1>
        <dl className="mt-3 grid grid-cols-3 gap-x-6 gap-y-1.5 text-xs">
          {meta.map(([label, value]) => (
            <div key={label}>
              <dt className="text-[11px] font-medium uppercase tracking-wide text-fg-muted">
                {label}
              </dt>
              <dd className="first-letter:uppercase">{value}</dd>
            </div>
          ))}
        </dl>
      </header>

      {omitted.length > 0 && (
        <p className="mt-3 border-l-2 border-fg pl-2 text-xs">
          Outdated sections omitted: {omitted.join(', ')}. Recompute to include
          them.
        </p>
      )}

      <Section number={++section} title="Transformation parameters">
        <DataTable>
          <tbody>
            <Row
              label="Scale factor"
              value={formatScale(solution.scale)}
              note={
                solution.sigmaScale !== undefined
                  ? sigma(formatScale(solution.sigmaScale))
                  : solution.scaleFixed
                    ? 'Held'
                    : undefined
              }
            />
            <Row
              label="Rotation (clockwise positive)"
              value={formatDMS(solution.rotation)}
              note={
                solution.sigmaRotation !== undefined
                  ? sigma(formatDMS(solution.sigmaRotation))
                  : undefined
              }
            />
            <Row
              label="Translation East (A3)"
              value={formatFixed(solution.translationE, 4)}
            />
            <Row
              label="Translation North (A4)"
              value={formatFixed(solution.translationN, 4)}
            />
            <Row
              label="Pairs in fit · redundancy"
              value={`${solution.pointCount} · r = ${solution.redundancy}`}
              note={solution.redundancy === 0 ? 'No redundancy' : undefined}
            />
            <Row
              label="RMS of residuals (total / N / E)"
              value={`${formatFixed(solution.rmsTotal, 4)} / ${formatFixed(solution.rmsN, 4)} / ${formatFixed(solution.rmsE, 4)}`}
            />
            <Row
              label="Standard error of unit weight σ₀"
              value={formatFixed(solution.sigma0, 4)}
              note={
                solution.sigma0 === undefined
                  ? 'Not estimable (no redundancy)'
                  : undefined
              }
            />
          </tbody>
        </DataTable>
        <div className="mt-3 grid grid-cols-2 gap-6 text-xs break-inside-avoid">
          <p className="font-mono leading-relaxed">
            N2 = A1·N1 − A2·E1 + A4
            <br />
            E2 = A2·N1 + A1·E1 + A3
          </p>
          <p className="font-mono leading-relaxed tabular-nums">
            A1 = {formatFixed(solution.a, 10)}
            <br />
            A2 = {formatFixed(solution.b, 10)}
            <br />
            A3 = {formatFixed(solution.c, 5)}
            <br />
            A4 = {formatFixed(solution.d, 5)}
          </p>
        </div>
      </Section>

      <Section number={++section} title="Residuals (Record → Field)">
        <DataTable>
          <thead>
            <tr>
              <Th>Record</Th>
              <Th>Field</Th>
              <Th numeric>
                <span className="normal-case">vN</span>
              </Th>
              <Th numeric>
                <span className="normal-case">vE</span>
              </Th>
              <Th numeric>Azimuth</Th>
              <Th numeric>Distance</Th>
              <Th>Status</Th>
            </tr>
          </thead>
          <tbody>
            {solution.residuals.map((residual) => {
              const pair = pairById.get(residual.id);
              if (!pair || residual.vn === null) return null;
              return (
                <tr key={residual.id}>
                  <Td mono>{pair.sys1Point}</Td>
                  <Td mono>{pair.sys2Point}</Td>
                  <Td numeric>{formatResidual(residual.vn)}</Td>
                  <Td numeric>{formatResidual(residual.ve)}</Td>
                  <Td numeric>{formatAzimuth(residual.azimuth)}</Td>
                  <Td numeric>{formatFixed(residual.total, 3)}</Td>
                  <Td className="capitalize">{residual.status}</Td>
                </tr>
              );
            })}
          </tbody>
        </DataTable>
      </Section>

      {hasDetection && (
        <Section number={++section} title="Blunder detection">
          <VerdictText
            blunderResults={blunderResults}
            dataSnoopingResults={dataSnoopingResults}
            pointPairs={pointPairs}
          />
          {blunderResults && !blunderResults.error && (
            <CombinatorialTable results={blunderResults} />
          )}
          {dataSnoopingResults && !dataSnoopingResults.error && (
            <DataSnoopingSummary results={dataSnoopingResults} />
          )}
        </Section>
      )}

      {transformedPoints.length > 0 && (
        <Section number={++section} title="Transformed points">
          <DataTable>
            <thead>
              <tr>
                <Th>Point</Th>
                <Th numeric>North</Th>
                <Th numeric>East</Th>
                <Th numeric>
                  Radial <span className="normal-case">σ</span>
                </Th>
              </tr>
            </thead>
            <tbody>
              {transformedPoints.map((point, index) => (
                <tr key={index}>
                  <Td mono>{point.newPoint}</Td>
                  <Td numeric>{point.n2}</Td>
                  <Td numeric>{point.e2}</Td>
                  <Td numeric>{point.stdDev || PLACEHOLDER}</Td>
                </tr>
              ))}
            </tbody>
          </DataTable>
          <p className="mt-1.5 text-xs text-fg-muted">
            Radial σ: predicted position uncertainty (about 63%; ×1.73 for 95%).
            Shown as {PLACEHOLDER} when the solution has no redundancy.
          </p>
        </Section>
      )}
    </article>
  );
};
